import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { Modal, Textarea, Checkbox, Input, Select } from '../../../shared/components'
import { AdminFilterTrigger, AdminFilterSidebar } from '../components'
import adminEmailTemplateService, {
  EmailTemplate,
  EmailTemplateFormValues
} from '../../../services/adminEmailTemplateService'
import CodeMirror from '@uiw/react-codemirror'
import { html } from '@codemirror/lang-html'

const CSS_HANDLES = [
  'adminEmailTemplates',
  'templatesHeader',
  'templatesTitle',
  'templatesSubtitle',
  'templatesActions',
  'templatesTable',
  'tableHeader',
  'tableRow',
  'templateInfo',
  'templateName',
  'templateType',
  'templateStatus',
  'templateActions',
  'viewButton',
  'editButton',
  'statusToggle',
  'statusActive',
  'statusInactive',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formGroup',
  'formLabel',
  'formInput',
  'formTextarea',
  'formSelect',
  'formRow',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'pagination',
  'paginationButton',
  'paginationInfo',
  'templatePreview',
  'previewSection',
  'previewLabel',
  'previewContent',
  'htmlPreview'
] as const

const AdminEmailTemplates: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)

  type EmailTemplateFormData = Omit<EmailTemplateFormValues, 'isActive'> & {
    isActive: string
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors }
  } = useForm<EmailTemplateFormData>({
    defaultValues: {
      name: '',
      subject: '',
      htmlContent: '',
      textContent: '',
      description: '',
      isActive: 'true'
    }
  })

  const watchedHtmlContent = watch('htmlContent')
  const [pagination, setPagination] = useState<{
    page: number
    limit: number
    total: number
    totalPages: number
  } | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [pendingStatusToggle, setPendingStatusToggle] = useState<string | null>(null)

  const defaultFilters = {
    search: '',
    isActive: undefined as boolean | undefined
  }

  const [filters, setFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))

  const itemsPerPage = 20

  const resolveErrorMessage = (err: any, defaultMessage: string): string => {
    if (err?.message) return err.message
    if (err?.response?.data?.message) return err.response.data.message
    if (typeof err?.response?.data === 'string') return err.response.data
    return defaultMessage
  }

  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const isLoadingRef = useRef(false)

  const fetchTemplates = useCallback(
    async (page: number) => {
      const filtersKey = JSON.stringify({
        search: filters.search.trim(),
        isActive: filters.isActive
      })

      const filtersChanged = previousFiltersRef.current !== filtersKey
      const pageChanged = previousPageRef.current !== page

      if (!filtersChanged && !pageChanged && hasLoaded) {
        return
      }

      if (isLoadingRef.current) {
        return
      }

      isLoadingRef.current = true
      setIsFetching(true)
      setError(null)

      try {
        const response = await adminEmailTemplateService.list({
          search: filters.search.trim() || undefined,
          isActive: filters.isActive,
          page,
          limit: itemsPerPage
        })

        setTemplates(response.templates)
        setPagination(response.pagination)
        setCurrentPage(response.pagination.page)
        setHasLoaded(true)
        previousFiltersRef.current = filtersKey
        previousPageRef.current = page
      } catch (err) {
        console.error('Erro ao buscar templates:', err)
        setError(
          resolveErrorMessage(err, 'Não foi possível carregar os templates. Tente novamente.')
        )
      } finally {
        setIsFetching(false)
        isLoadingRef.current = false
      }
    },
    [filters, hasLoaded, itemsPerPage]
  )

  useEffect(() => {
    fetchTemplates(currentPage)
  }, [currentPage, fetchTemplates])

  const handleOpenFilters = () => {
    setDraftFilters(filters)
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setDraftFilters(filters)
    setIsFilterSidebarOpen(false)
  }

  const handleApplyFilters = () => {
    setFilters(draftFilters)
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    setDraftFilters(defaultFilters)
    setFilters(defaultFilters)
    setCurrentPage(1)
  }

  const hasActiveFilters =
    filters.search.trim() !== '' || filters.isActive !== undefined

  const handleEditTemplate = (template: EmailTemplate) => {
    setActionError(null)
    setEditingTemplate(template)
    reset({
      name: template.name || '',
      subject: template.subject || '',
      htmlContent: template.htmlContent || '',
      textContent: template.textContent || '',
      description: template.description || '',
      isActive: template.isActive ? 'true' : 'false'
    })
    const processed = processPreviewHtml(template.htmlContent || '')
    setPreviewHtml(processed)
    setIsModalOpen(true)
  }

  // Função para substituir variáveis do template no preview
  const processPreviewHtml = useCallback((html: string): string => {
    if (!html) return ''

    // Valores padrão para o preview
    const defaultValues: Record<string, string> = {
      companyName: 'BaterPower',
      frontendUrl: 'http://localhost:3000',
      supportEmail: 'suporte@baterpower.com.br',
      supportPhone: '(00) 0000-0000',
      userName: 'João Silva',
      userEmail: 'joao.silva@example.com',
      orderNumber: 'ORD-12345',
      orderId: '12345',
      orderDate: new Date().toLocaleDateString('pt-BR'),
      orderTotal: 'R$ 1.500,00',
      orderSubtotal: 'R$ 1.400,00',
      orderShipping: 'R$ 100,00',
      orderDiscount: 'R$ 0,00',
      orderStatus: 'Confirmado',
      trackingCode: 'BR123456789BR',
      orderUrl: 'http://localhost:3000/pedidos',
      resetUrl: 'http://localhost:3000/reset-password?token=example-token',
      paymentAmount: 'R$ 1.500,00',
      paymentMethod: 'Pix',
      paymentExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR'),
      pixCode: '00020126360014BR.GOV.BCB.PIX...',
      pixQrCode: 'data:image/png;base64,iVBORw0KGgoAAAANS...',
      boletoUrl: 'http://localhost:3000/payment/boleto/12345',
      boletoBarcode: '34191.09008 01234.567890 12345.678901 2 12345678901234',
      refundAmount: 'R$ 1.500,00',
      refundMethod: 'Cartão de Crédito',
      refundEstimatedDays: '5',
      returnNumber: 'RET-12345',
      returnStatus: 'Em Processamento',
      returnTrackingCode: 'BR987654321BR',
      itemsList: '• Bateria 60Ah - Qtd: 2 - R$ 700,00<br>• Bateria 70Ah - Qtd: 1 - R$ 800,00',
      shippingAddress: 'Av. José de Sousa Campos, 2086 - Nova Campinas, Campinas - SP, 13025-320',
      ticketNumber: 'TKT-12345'
    }

    let processedHtml = html

    // Substituir variáveis no formato {{variableName}}
    for (const [key, value] of Object.entries(defaultValues)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
      processedHtml = processedHtml.replace(regex, value)
    }

    // Tratamento especial para o logo: substituir {{frontendUrl}}/assets/logo-cor-com-slogan.png
    // por /assets/logo-cor-com-slogan.png (caminho relativo para o preview)
    processedHtml = processedHtml.replace(
      /\{\{frontendUrl\}\}\/assets\/logo-cor-com-slogan\.png/g,
      '/assets/logo-cor-com-slogan.png'
    )

    // Remover variáveis não substituídas (opcional - deixa vazio)
    processedHtml = processedHtml.replace(/\{\{[\w]+\}\}/g, '')

    return processedHtml
  }, [])

  // Update preview when htmlContent changes
  useEffect(() => {
    if (watchedHtmlContent !== undefined) {
      const processed = processPreviewHtml(watchedHtmlContent)
      setPreviewHtml(processed)
    }
  }, [watchedHtmlContent, processPreviewHtml])

  const handleToggleStatus = async (templateId: string, currentStatus: boolean) => {
    setPendingStatusToggle(templateId)
    setActionError(null)

    try {
      await adminEmailTemplateService.updateStatus(templateId, !currentStatus)
      await fetchTemplates(currentPage)
    } catch (err) {
      console.error('Erro ao atualizar status do template:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível atualizar o status do template.')
      )
    } finally {
      setPendingStatusToggle(null)
    }
  }

  const onSubmitTemplate = async (data: EmailTemplateFormData) => {
    if (!editingTemplate) return

    setIsSaving(true)
    setActionError(null)

    const formValues: EmailTemplateFormValues = {
      name: data.name || undefined,
      subject: data.subject || undefined,
      htmlContent: data.htmlContent || undefined,
      textContent: data.textContent || undefined,
      description: data.description || undefined,
      isActive: data.isActive === 'true'
    }

    try {
      await adminEmailTemplateService.update(editingTemplate.id, formValues)
      await fetchTemplates(currentPage)
      setIsModalOpen(false)
      setEditingTemplate(null)
      reset({
        name: '',
        subject: '',
        htmlContent: '',
        textContent: '',
        description: '',
        isActive: 'true'
      })
    } catch (err) {
      console.error('Erro ao salvar template:', err)
      const message = resolveErrorMessage(
        err,
        'Não foi possível salvar o template. Verifique os dados e tente novamente.'
      )
      setActionError(message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  const totalPages = pagination?.totalPages ?? 1
  const totalItems = pagination?.total ?? templates.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? itemsPerPage
  const startIndex = totalItems === 0 ? 0 : (currentPageNumber - 1) * limit
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + templates.length, totalItems)
  const showLoadingOverlay = hasLoaded && isFetching
  const activeFiltersDescriptions = [
    filters.isActive !== undefined
      ? `status: ${filters.isActive ? 'ativos' : 'inativos'}`
      : null,
    filters.search.trim() ? `busca: "${filters.search.trim()}"` : null
  ]
    .filter(Boolean)
    .join(' • ')

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-80" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="h-16 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (!hasLoaded && isFetching) {
    return renderSkeleton()
  }

  if (error && !hasLoaded && !isFetching) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => fetchTemplates(1)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminEmailTemplates} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            <div className={`${handles.templatesHeader}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1
                    className={`${handles.templatesTitle} text-3xl font-bold text-gray-900 mb-2`}
                  >
                    Templates de Email
                  </h1>
                  <p className={`${handles.templatesSubtitle} text-lg text-gray-600`}>
                    Gerencie os templates de email transacionais
                  </p>
                </div>
              </div>
            </div>

            {actionError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {actionError}
              </div>
            )}

            {error && hasLoaded && !isFetching && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchTemplates(currentPageNumber)}
                    className="inline-flex items-center text-red-700 underline hover:text-red-800"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            <div className={`${handles.templatesActions} rounded-xl bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}
                    </span>{' '}
                    de <span className="font-semibold text-gray-900">{totalItems}</span> templates
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <AdminFilterTrigger
                  onClick={handleOpenFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </div>

            <div className="rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className={`${handles.templatesTable} w-full`}>
                  <thead className={`${handles.tableHeader} bg-gray-50`}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="sticky right-0 z-20 bg-gray-50 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-32">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {templates.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-10 text-center text-sm text-gray-500"
                        >
                          Nenhum template encontrado para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      templates.map((template) => (
                        <tr key={template.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.templateInfo}`}>
                              <div
                                className={`${handles.templateName} text-sm font-medium text-gray-900`}
                              >
                                {template.name}
                              </div>
                              {template.description && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {template.description.substring(0, 60)}...
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            <code className={`${handles.templateType} text-xs bg-gray-100 px-2 py-1 rounded`}>
                              {template.type}
                            </code>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <button
                              onClick={() =>
                                handleToggleStatus(template.id, template.isActive)
                              }
                              disabled={pendingStatusToggle === template.id}
                              className={`${handles.statusToggle} ${
                                template.isActive
                                  ? `${handles.statusActive} bg-green-100 text-green-800`
                                  : `${handles.statusInactive} bg-red-100 text-red-800`
                              } rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                template.isActive
                                  ? 'focus-visible:ring-green-300'
                                  : 'focus-visible:ring-red-300'
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {pendingStatusToggle === template.id
                                ? 'Atualizando...'
                                : template.isActive
                                ? 'Ativo'
                                : 'Inativo'}
                            </button>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-32">
                            <div
                              className={`${handles.templateActions} flex space-x-2 justify-center`}
                            >
                              <button
                                onClick={() => handleEditTemplate(template)}
                                title="Editar template"
                                aria-label="Editar template"
                                className={`${handles.editButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`}
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.8}
                                    d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z"
                                  />
                                </svg>
                                <span className="sr-only">Editar template</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div
                  className={`${handles.pagination} bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200`}
                >
                  <div className={`${handles.paginationInfo} text-sm text-gray-700`}>
                    {totalItems > 0
                      ? `Mostrando ${startIndex + 1} a ${endIndex} de ${totalItems} templates`
                      : 'Nenhum template encontrado'}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPageNumber === 1}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`${handles.paginationButton} px-3 py-1 border rounded-md ${
                          page === currentPageNumber
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPageNumber === totalPages}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdminFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        title="Filtros de Templates"
      >
        <div className="space-y-4">
          <div className={`${handles.formGroup}`}>
            <label htmlFor="search" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Buscar
            </label>
            <Input
              type="text"
              id="search"
              name="search"
              value={draftFilters.search}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, search: e.target.value }))
              }
              placeholder="Nome, tipo ou assunto..."
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label
              htmlFor="isActive"
              className={`${handles.formLabel} block text-sm font-medium text-gray-700`}
            >
              Status
            </label>
            <Select
              id="isActive"
              name="isActive"
              value={
                draftFilters.isActive === undefined
                  ? ''
                  : draftFilters.isActive
                  ? 'true'
                  : 'false'
              }
              onChange={(value) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  isActive: value === '' ? undefined : value === 'true'
                }))
              }
              options={[
                { value: '', label: 'Todos' },
                { value: 'true', label: 'Ativos' },
                { value: 'false', label: 'Inativos' }
              ]}
              placeholder="Todos"
              className={`${handles.formSelect}`}
            />
          </div>
        </div>
      </AdminFilterSidebar>

      {/* Modal de Edição */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingTemplate(null)
          setActionError(null)
          setPreviewHtml('')
          reset({
            name: '',
            subject: '',
            htmlContent: '',
            textContent: '',
            description: '',
            isActive: 'true'
          })
        }}
        title={editingTemplate ? `Editar: ${editingTemplate.name}` : 'Novo Template'}
        size="full"
        overlayClassName="bg-black bg-opacity-50"
        contentClassName="max-w-[98vw] max-h-[98vh] m-4"
        bodyStyle={{ padding: '1.5rem', height: 'calc(98vh - 120px)', display: 'flex', flexDirection: 'column' }}
      >
        {editingTemplate && (
          <form onSubmit={handleSubmit(onSubmitTemplate)} className="h-full flex flex-col">
            {actionError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {actionError}
              </div>
            )}

            <div className="flex-1 grid grid-cols-2 gap-6 overflow-hidden">
              {/* Coluna Esquerda - Formulário */}
              <div className={`${handles.modalBody} space-y-4 overflow-y-auto pr-2`}>
                <div className={`${handles.formGroup}`}>
                  <label htmlFor="name" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Nome *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    {...register('name', {
                      required: 'Nome é obrigatório'
                    })}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </div>

                <div className={`${handles.formGroup}`}>
                  <label htmlFor="subject" className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Assunto *
                  </label>
                  <Input
                    type="text"
                    id="subject"
                    {...register('subject', {
                      required: 'Assunto é obrigatório'
                    })}
                    error={!!errors.subject}
                    helperText={errors.subject?.message}
                  />
                </div>

                <div className={`${handles.formGroup}`}>
                  <label
                    htmlFor="description"
                    className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}
                  >
                    Descrição
                  </label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    rows={3}
                  />
                </div>

                <div className={`${handles.formGroup}`}>
                  <label
                    htmlFor="htmlContent"
                    className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}
                  >
                    Conteúdo HTML *
                  </label>
                  <Controller
                    name="htmlContent"
                    control={control}
                    rules={{
                      required: 'Conteúdo HTML é obrigatório'
                    }}
                    render={({ field }) => (
                      <>
                        <div className="border border-gray-300 rounded-lg overflow-hidden">
                          <CodeMirror
                            value={field.value}
                            height="400px"
                            extensions={[html()]}
                            onChange={(value: string) => {
                              field.onChange(value)
                              const processed = processPreviewHtml(value)
                              setPreviewHtml(processed)
                            }}
                            basicSetup={{
                              lineNumbers: true,
                              foldGutter: true,
                              dropCursor: false,
                              allowMultipleSelections: false,
                              autocompletion: true,
                              bracketMatching: true,
                              closeBrackets: true,
                              highlightSelectionMatches: true
                            }}
                          />
                        </div>
                        {errors.htmlContent && (
                          <p className="mt-1 text-sm text-red-600">{errors.htmlContent.message}</p>
                        )}
                        <p className="mt-2 text-xs text-gray-500">
                          Use variáveis como {'{{'}companyName{'}}'}, {'{{'}userName{'}}'}, etc.
                        </p>
                      </>
                    )}
                  />
                </div>

                <div className={`${handles.formGroup}`}>
                  <label
                    htmlFor="textContent"
                    className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}
                  >
                    Conteúdo Texto (opcional)
                  </label>
                  <Textarea
                    id="textContent"
                    {...register('textContent')}
                    rows={6}
                    className="font-mono"
                  />
                </div>

                <div className={`${handles.formGroup}`}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value === 'true'}
                        onChange={(e) => field.onChange(e.target.checked ? 'true' : 'false')}
                        label="Template ativo"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Coluna Direita - Preview */}
              <div className="flex flex-col overflow-hidden border-l border-gray-200 pl-6">
                <label className={`${handles.previewLabel} block text-sm font-medium text-gray-700 mb-3`}>
                  Preview do HTML
                </label>
                <div className="flex-1 overflow-auto border border-gray-300 rounded-md bg-white">
                  <div
                    className={`${handles.htmlPreview} min-h-full p-4`}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>
            </div>

            <div className={`${handles.modalFooter} mt-6 flex justify-end space-x-3 border-t border-gray-200 pt-4`}>
              <button
                type="button"
                onClick={() => {
                  setIsModalOpen(false)
                  setEditingTemplate(null)
                  setActionError(null)
                  setPreviewHtml('')
                  reset({
                    name: '',
                    subject: '',
                    htmlContent: '',
                    textContent: '',
                    description: '',
                    isActive: 'true'
                  })
                }}
                className={`${handles.cancelButton} rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`${handles.saveButton} rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </AdminLayout>
  )
}

export default AdminEmailTemplates

